import { useState, useEffect } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useIntl, FormattedMessage } from 'react-intl'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import FlexColumn from '../../shared/components/layouts/FlexColumn'
import Flex from '../../shared/components/layouts/Flex'
import FormikTextField from '../../shared/components/formik/FormikTextField'
import useTwoFactorAuth from '../../shared/resources/api/v1/auth/useTwoFactorAuth'

const TwoFactorSetupDialog = ({ open, onClose, onActivated }) => {
  const intl = useIntl()
  const { setup, activate } = useTwoFactorAuth()
  const [setupData, setSetupData] = useState(null)
  const [setupLoading, setSetupLoading] = useState(false)
  const [setupError, setSetupError] = useState(null)

  useEffect(() => {
    if (open) {
      setSetupLoading(true)
      setSetupError(null)
      setSetupData(null)
      setup()
        .then(({ twoFactor }) => {
          setSetupData(twoFactor)
        })
        .catch(() => {
          setSetupError(intl.formatMessage({ id: 'settings.twoFactor.setup.error' }))
        })
        .finally(() => {
          setSetupLoading(false)
        })
    }
  }, [open, setup, intl])

  const validationSchema = Yup.object({
    code: Yup.string()
      .matches(/^\d{6}$/, intl.formatMessage({ id: 'settings.twoFactor.error.format' }))
      .required(intl.formatMessage({ id: 'login.error.required' })),
  })

  const formik = useFormik({
    initialValues: { code: '' },
    validationSchema,
    onSubmit: (values, { setSubmitting, setStatus }) => {
      setStatus(null)
      activate(values.code)
        .then(() => {
          onActivated()
        })
        .catch(() => {
          setStatus(intl.formatMessage({ id: 'settings.twoFactor.error.invalid' }))
        })
        .finally(() => {
          setSubmitting(false)
        })
    },
  })

  const handleClose = () => {
    formik.resetForm()
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <FormattedMessage id="settings.twoFactor.setup.title" />
      </DialogTitle>
      <DialogContent>
        <FlexColumn gap={2} sx={{ mt: 1 }}>
          {setupLoading && (
            <Flex justifyContent="center" fullWidth>
              <CircularProgress />
            </Flex>
          )}

          {setupError && (
            <Alert severity="error">{setupError}</Alert>
          )}

          {setupData && (
            <>
              <Typography variant="body2">
                <FormattedMessage id="settings.twoFactor.setup.scanQrCode" />
              </Typography>

              <Flex justifyContent="center" fullWidth>
                <Box
                  dangerouslySetInnerHTML={{ __html: setupData.qrCodeSvg }}
                  sx={{ bgcolor: 'white', p: 1, borderRadius: 1, '& svg': { maxWidth: 200, height: 'auto', display: 'block' } }}
                />
              </Flex>

              <Typography variant="caption" color="text.secondary">
                <FormattedMessage id="settings.twoFactor.setup.manualEntry" />
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {setupData.provisioningUri}
              </Typography>

              <Typography variant="body2" sx={{ mt: 1 }}>
                <FormattedMessage id="settings.twoFactor.setup.enterCode" />
              </Typography>

              {formik.status && (
                <Alert severity="error">{formik.status}</Alert>
              )}

              <Box component="form" onSubmit={formik.handleSubmit} sx={{ width: '100%' }}>
                <FormikTextField
                  formik={formik}
                  name="code"
                  label={<FormattedMessage id="settings.twoFactor.setup.codeLabel" />}
                  autoFocus
                  autoComplete="one-time-code"
                />
              </Box>
            </>
          )}
        </FlexColumn>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose} disabled={formik.isSubmitting}>
          <FormattedMessage id="app.actions.cancel.label" />
        </Button>
        {setupData && (
          <Button
            variant="contained"
            onClick={formik.handleSubmit}
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? (
              <CircularProgress size={20} />
            ) : (
              <FormattedMessage id="settings.twoFactor.setup.submit" />
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default TwoFactorSetupDialog
